/**
 * XRecord utilities for persisting photomarker data.
 *
 * Stores the last photomarker number in an XRecord within the Named Objects
 * Dictionary so it persists in the DWG file and can be retrieved on file reopen,
 * avoiding slow entity scanning.
 */

import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { openAs } from "../core/oda";
import { OpenMode } from "../core/constants";

const PHOTOMARKER_DICT_NAME = "PHOTOMARKER_DATA";
const LAST_NUMBER_KEY = "LAST_NUMBER";
const DXF_INT32 = 90; // DXF code for 32-bit integer

/**
 * Save the last photomarker number to an XRecord in the Named Objects Dictionary.
 *
 * @param module The WASM module
 * @param db The database to save to
 * @param lastNumber The last marker number to persist
 * @returns true on success
 */
export function savePhotomarkerLastNumber(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  lastNumber: number,
): boolean {
  try {
    // Get Named Objects Dictionary
    const mainDictId = db.getNamedObjectsDictionaryId();
    const mainDict = openAs(
      mainDictId,
      module.OdDbDictionary,
      OpenMode.kForWrite,
    );

    // Get or create PHOTOMARKER_DATA dictionary
    const dictNameStr = new module.OdString(PHOTOMARKER_DICT_NAME);
    let myDict: DrawingModule.OdDbDictionary;

    if (!mainDict.has1(dictNameStr)) {
      // Create new dictionary and add to main dict
      // After setAt, the main dict owns it - don't delete separately
      myDict = module.OdDbDictionary.createObject();
      mainDict.setAt(dictNameStr, myDict);
    } else {
      // Use getAt1 which takes OpenMode and returns OdDbObject directly
      const dictObj = mainDict.getAt1(dictNameStr, OpenMode.kForWrite);
      myDict = module.OdDbDictionary.cast(dictObj);
    }

    // Get or create LAST_NUMBER XRecord
    const keyStr = new module.OdString(LAST_NUMBER_KEY);
    let xrec: DrawingModule.OdDbXrecord;

    if (!myDict.has1(keyStr)) {
      // Create new XRecord and add to dictionary
      // After setAt, the dict owns it - don't delete separately
      xrec = module.OdDbXrecord.createObject();
      myDict.setAt(keyStr, xrec);
    } else {
      // Use getAt1 which takes OpenMode and returns OdDbObject directly
      const xrecObj = myDict.getAt1(keyStr, OpenMode.kForWrite);
      xrec = module.OdDbXrecord.cast(xrecObj);
    }

    // Store the integer value (DXF code 90 = 32-bit integer)
    const resbuf = module.OdResBuf.newRb(DXF_INT32);
    resbuf.setInt32(lastNumber);
    xrec.setFromRbChain(resbuf, db);
    deleteObj(resbuf);

    // Cleanup - only delete objects we opened, not ones we created and added via setAt
    // Objects added via setAt are owned by their parent dictionary
    deleteObj(xrec);
    deleteObj(keyStr);
    deleteObj(myDict);
    deleteObj(dictNameStr);
    deleteObj(mainDict);

    return true;
  } catch (error) {
    console.error("[xrecord] savePhotomarkerLastNumber ERROR:", error);
    return false;
  }
}

/**
 * Get the last photomarker number from an XRecord in the Named Objects Dictionary.
 *
 * @param module The WASM module
 * @param db The database to read from
 * @param defaultValue Value to return if no XRecord found (default: 0)
 * @returns The stored last number, or defaultValue if not found
 */
export function getPhotomarkerLastNumber(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  defaultValue: number = 0,
): number {
  try {
    const mainDictId = db.getNamedObjectsDictionaryId();
    const mainDict = openAs(
      mainDictId,
      module.OdDbDictionary,
      OpenMode.kForRead,
    );

    const dictNameStr = new module.OdString(PHOTOMARKER_DICT_NAME);
    if (!mainDict.has1(dictNameStr)) {
      deleteObj(dictNameStr);
      deleteObj(mainDict);
      return defaultValue;
    }

    // Use getAt1 which takes OpenMode and returns OdDbObject directly
    const dictObj = mainDict.getAt1(dictNameStr, OpenMode.kForRead);
    const myDict = module.OdDbDictionary.cast(dictObj);

    const keyStr = new module.OdString(LAST_NUMBER_KEY);
    if (!myDict.has1(keyStr)) {
      deleteObj(keyStr);
      deleteObj(dictNameStr);
      deleteObj(myDict);
      deleteObj(mainDict);
      return defaultValue;
    }

    // Use getAt1 which takes OpenMode and returns OdDbObject directly
    const xrecObj = myDict.getAt1(keyStr, OpenMode.kForRead);
    const xrec = module.OdDbXrecord.cast(xrecObj);

    // Use iterator to read the data (avoids problematic pStatus parameter in rbChain)
    const iterator = xrec.newIterator(db);
    iterator.start();

    let result = defaultValue;
    if (!iterator.done() && iterator.curRestype() === DXF_INT32) {
      const rb = iterator.getCurResbuf(db);
      result = rb.getInt32();
      deleteObj(rb);
    }

    // Cleanup
    deleteObj(iterator);
    deleteObj(keyStr);
    deleteObj(dictNameStr);
    deleteObj(xrec);
    deleteObj(myDict);
    deleteObj(mainDict);

    return result;
  } catch (error) {
    console.error("[xrecord] getPhotomarkerLastNumber ERROR:", error);
    return defaultValue;
  }
}
